import { normalizePermissionsRegistry } from "../utils/resolveUserPermissions";
import axios from "./axiosConfig";
import api from "../config/gatewayConfig";

const PERMISSIONS_JSON_URL = "https://x.acridite.cc/wcp/permissions.json";
const PERMISSIONS_REGISTRY_STORAGE_KEY = "adminPermissionsRegistry";

let cachedRegistry = null;
let inflightRequest = null;

function getAuthHeaders() {
  const rawToken = sessionStorage.getItem("token");
  if (!rawToken) {
    return null;
  }
  try {
    const token = rawToken.startsWith('"') || rawToken.startsWith("{")
      ? JSON.parse(rawToken)
      : rawToken;
    if (!token) {
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  } catch {
    return null;
  }
}

function readSessionRegistry() {
  try {
    const raw = sessionStorage.getItem(PERMISSIONS_REGISTRY_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return normalizePermissionsRegistry(parsed);
  } catch {
    return null;
  }
}

function writeSessionRegistry(registry) {
  sessionStorage.setItem(PERMISSIONS_REGISTRY_STORAGE_KEY, JSON.stringify(registry));
}

async function fetchPermissionsFallback() {
  const response = await fetch(PERMISSIONS_JSON_URL, { cache: "no-store" });
  if (!response.ok) {
    throw new Error(`Permissions fetch failed (${response.status})`);
  }
  const payload = await response.json();
  return normalizePermissionsRegistry(payload);
}

async function fetchPermissionsFromApi() {
  const headers = getAuthHeaders();
  if (!headers) {
    return null;
  }

  const response = await axios.post(
    api.adminPermissionsGet,
    {},
    { headers, skipGlobalErrorHandler: true }
  );
  const payload = response?.data;
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null;
  }
  if (Object.keys(payload).length === 0) {
    return null;
  }
  return normalizePermissionsRegistry(payload);
}

export function hydratePermissionsRegistryFromSession() {
  if (cachedRegistry) {
    return cachedRegistry;
  }
  const fromSession = readSessionRegistry();
  if (fromSession) {
    cachedRegistry = fromSession;
    return cachedRegistry;
  }
  return null;
}

export async function fetchPermissionsRegistry({ forceRefresh = false } = {}) {
  if (!getAuthHeaders()) {
    throw new Error("Sessione non autenticata.");
  }

  if (!forceRefresh) {
    const hydrated = hydratePermissionsRegistryFromSession();
    if (hydrated) {
      return hydrated;
    }
  }

  if (!forceRefresh && inflightRequest) {
    return inflightRequest;
  }

  inflightRequest = (async () => {
    try {
      const fromApi = await fetchPermissionsFromApi();
      if (fromApi) {
        cachedRegistry = fromApi;
        writeSessionRegistry(cachedRegistry);
        return cachedRegistry;
      }
    } catch {
      // Fall back to the legacy static JSON host.
    }

    cachedRegistry = await fetchPermissionsFallback();
    writeSessionRegistry(cachedRegistry);
    return cachedRegistry;
  })().finally(() => {
    inflightRequest = null;
  });

  return inflightRequest;
}

export async function updatePermissionsRegistry(registry) {
  const headers = getAuthHeaders();
  if (!headers) {
    throw new Error("Sessione non autenticata.");
  }

  const response = await axios.post(api.adminPermissionsUpdate, registry, {
    headers,
    skipGlobalErrorHandler: true,
  });
  const payload = response?.data;
  cachedRegistry = normalizePermissionsRegistry(payload ?? registry);
  writeSessionRegistry(cachedRegistry);
  return cachedRegistry;
}

export function getCachedPermissionsRegistry() {
  return cachedRegistry ?? readSessionRegistry();
}

export function clearPermissionsRegistryCache() {
  cachedRegistry = null;
  inflightRequest = null;
  sessionStorage.removeItem(PERMISSIONS_REGISTRY_STORAGE_KEY);
}
